import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { omit } from 'ramda';
import WarningIcon from '@moda/icons/warning-20';
import { Stack, Text } from '@moda/om';
import { scrollIntoViewIfNeeded } from '../../../lib/scrollIntoViewIfNeeded';
import { reportError } from '../../../lib/reportError';
import { AccountOrderDetailPageState } from '../../../constants';
import { useUrlParams } from '../../../hooks/useUrlParams';
import { RestAddress } from '../../../hooks/useAddress';
import { useErrorModal } from '../../../hooks/useErrorModal';
import {
  usePreviewOrderShippingAddressUpdate,
  useUpdateOrderShippingAddress
} from '../../../hooks/useUpdateOrderShippingAddress';
import { useOrderDetails } from '../../OrderDetails';
import {
  ShippingAddressSelector,
  Mode as ShippingAddressSelectorMode,
  RefInterface as ShippingAddressSelectorRefInterface
} from '../ShippingAddressSelector';
import { OrderDetailsShippingAddressConfirmation } from './OrderDetailsShippingAddressConfirmation';

import './OrderDetailsShippingAddress.scss';

enum Mode {
  Resting,
  Changing,
  Confirming
}

export const OrderDetailsShippingAddress: React.FC = () => {
  const addressSelectorRef = useRef<ShippingAddressSelectorRefInterface>(null);
  const history = useHistory();
  const { getHrefFor, params } = useUrlParams();
  const { orderSummary, orderNumber } = useOrderDetails();
  const { showErrorModal } = useErrorModal();
  const { updateOrderShippingAddress } = useUpdateOrderShippingAddress(orderNumber);

  const { previewOrderShippingAddressUpdate } = usePreviewOrderShippingAddressUpdate(orderNumber);

  const disabledAddressIds = useMemo(
    () => orderSummary?.attributes.restrictedAddressIds?.map(String),
    [orderSummary]
  );

  const [mode, setMode] = useState(Mode.Resting);

  const onModeChange = useCallback(
    (
      previousMode: ShippingAddressSelectorMode | undefined,
      nextMode: ShippingAddressSelectorMode
    ) => {
      if (nextMode !== ShippingAddressSelectorMode.Saving) {
        scrollIntoViewIfNeeded(document.getElementById('shipping_address'));
      }

      if (nextMode === ShippingAddressSelectorMode.Resting && mode === Mode.Changing) {
        setMode(Mode.Resting);
      }

      if (nextMode !== ShippingAddressSelectorMode.Resting && mode === Mode.Resting) {
        setMode(Mode.Changing);
      }
    },
    [mode]
  );

  const onSubmit = useCallback(
    async (address: RestAddress) => {
      await previewOrderShippingAddressUpdate(address.id);
      setMode(Mode.Confirming);
    },
    [previewOrderShippingAddressUpdate]
  );

  const addressId = orderSummary?.attributes.selectedAddressId;

  const onClickProceed = useCallback(() => {
    updateOrderShippingAddress(String(addressId))
      .then(() => setMode(Mode.Resting))
      .catch(error => {
        reportError(error);
        setMode(Mode.Resting);
        showErrorModal();
      });
  }, [addressId, updateOrderShippingAddress, showErrorModal]);

  useEffect(() => {
    if (
      params.state === AccountOrderDetailPageState.ChangingShippingAddress &&
      mode === Mode.Resting
    ) {
      addressSelectorRef.current?.change();
      history.replace(getHrefFor(omit(['state'], params)));
    }
  }, [mode, params, history, getHrefFor]);

  if (!orderSummary) return null;

  const { isAddressChangeable, address, selectedAddressId } = orderSummary.attributes;

  return (
    <>
      <Stack className="OrderDetailsShippingAddress" id="shipping_address" space={3}>
        <Stack space={2}>
          <Text treatment="h5">
            {mode === Mode.Changing ? 'Change Shipping Address for Open Items' : 'Shipping Address'}
          </Text>
          {mode === Mode.Changing && (
            <Stack direction="horizontal" space={1} alignItems="center">
              <Text color="code-red">
                <WarningIcon />
              </Text>
              <Text treatment="body1" color="code-red">
                This change will apply to all open items on this order
              </Text>
            </Stack>
          )}
        </Stack>
        <ShippingAddressSelector
          ref={addressSelectorRef}
          theme="box"
          readOnly={!isAddressChangeable}
          disabledAddressIds={disabledAddressIds}
          defaultAddress={address}
          defaultAddressId={selectedAddressId?.toString()}
          onModeChange={onModeChange}
          onSubmit={onSubmit}
        />
      </Stack>
      <OrderDetailsShippingAddressConfirmation
        show={mode === Mode.Confirming}
        onClose={() => setMode(Mode.Resting)}
        onProceed={onClickProceed}
      />
    </>
  );
};
